import ReceiptForm from "../Payment/CreateReceipt";
import { getMethodLabel } from "../Payment/paymentNormalizer";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import MainText from "../../components/MainText";
import {
  Delete,
  Search,
  Refresh,
  Event,
  AccessTime,
  Summarize,
  Edit,
  FiberManualRecord,
  Close,
  Preview,
  NoteAlt,
  ErrorOutline,
  CheckCircle,
  ReceiptLong,
  Description,
  InsertDriveFile,
  InfoOutlined,
  WarningAmber,
  AttachFile,
  LinkOff,
  LocalShipping,
  TrendingUp,
  Receipt,
  Cancel,
  AccountBalance,
  Download,
  Business,
  AddLink,
} from "@mui/icons-material";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Grid,
  Paper,
  Avatar,
  Stack,
  CircularProgress,
} from "@mui/material";
import DatePick from "../../components/DatePick";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addTab, mergeFlow } from "../../tabs/tabsSlice";
import API, { SettlementsAPI, RBAC } from "../../services/api";
import { DataGrid } from "@mui/x-data-grid";
import CleanningButton from "../../components/CleanningButton";
import CustomSwitch from "../../components/CustomSwitch";
import CustomModal from "../../components/CustomModal";
import EditInvoice from "./EditInvoice";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import GridFooterSummary from "../../components/GridSummary";
import PaymentDetailTabbed from "../../components/PaymentDetailDialog";
import { useAuth } from "../../auth/AuthProvider";
import { hasAny } from "../../auth/permissions";
import {
  normalizeInvoicePaymentStatus,
  normalizePaymentFilter,
  normalizePaymentStatus,
  PAYMENT_FILTER_ALL,
} from '../../utils/paymentStatus';
import { extractApiError } from '../../utils/apiError';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const toISODate = (v) => {
  if (!v) return null;

  // Ya viene como string 'YYYY-MM-DD'
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Es un Date
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return null;
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Cualquier otra cosa que acepte new Date
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const BANK_REQUISITION_FLOW = "BANK_REQUISITION";
const bankPaymentStatusLabels = {
  PENDING_REQUISITION: "Pendiente Abasto",
  PENDING_BANK_CAPTURE: "Pendiente captura bancaria",
  CAPTURED: "Captada",
  PARTIALLY_PAID_BANK: "Parcial",
  PAID_BANK: "Liquidada via banco",
};

const bankRequisitionStatusLabels = {
  PENDING_ABASTO: "Pendiente de cargar",
  ABASTO_LOADED: "Cargada",
  PENDING_UPLOAD: "Pendiente de cargar",
  UPLOADED: "Cargada",
  PARTIALLY_INVOICED: "Parcialmente facturada",
  INVOICED: "Facturada",
  EXPIRED_MONTHLY_CLOSE: "Vencida por cierre mensual",
  CANCELLED: "Cancelada",
};

const abastoStatusColors = {
  PENDING: "warning",
  PARTIALLY_PAID: "info",
  PAID: "success",
  OVERDUE: "error",
  CANCELLED: "default",
};

const bankPaymentStatusColors = {
  PENDING_REQUISITION: "warning",
  PENDING_BANK_CAPTURE: "warning",
  CAPTURED: "info",
  PARTIALLY_PAID_BANK: "info",
  PAID_BANK: "success",
};

const isBankRequisitionInvoice = (row) => String(row?.payment_flow_type || "").toUpperCase() === BANK_REQUISITION_FLOW;

const formatYMD = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";
  // 1) Coincidencia exacta YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  // 2) ISO o cualquier cosa que empiece con YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, Y, M, D] = m;
    return `${D}/${M}/${Y}`;
  }
  // 3) Último intento: Date nativa
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  // 4) No se pudo formatear
  return "Sin fecha";
};
const formatYMDHMS = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";

  // 1) Coincidencia exacta YYYY-MM-DD HH:mm:ss (o similar)
  // Esta regex es más flexible para incluir T, Z o espacios
  const fullMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(Z)?)?/
  );

  if (fullMatch) {
    // Usaremos la Date nativa para manejar la conversión de zona horaria (como 'Z' o ISO)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");

      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    }
  }

  // 2) Último intento: Date nativa para cualquier otro formato válido
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  // 3) No se pudo formatear
  return "Sin fecha";
};
// ===== Helpers (URL o File) =====
const isImageLike = (f) => {
  if (!f) return false;
  const name = typeof f === 'string' ? f : f.name || '';
  const type = typeof f === 'string' ? '' : f.type || '';
  return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};
const isPdfLike = (f) => {
  if (!f) return false;
  const name = typeof f === 'string' ? f : f.name || '';
  const type = typeof f === 'string' ? '' : f.type || '';
  return type === 'application/pdf' || /\.pdf($|\?)/i.test(name);
};
const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000/').replace(/\/+$/, '/');
const absolutizeUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.replace(/^\/+/, '')}`;
};
const getPreviewUrl = (f) => {
  if (typeof f === 'string') {
    const abs = absolutizeUrl(f);
    const sep = abs.includes('?') ? '&' : '?';
    // Cache-buster para evitar que el visor PDF se quede con un 304 sin renderizar
    return `${abs}${sep}v=${Date.now()}`;
  }
  try {
    return URL.createObjectURL(f);
  } catch {
    return '';
  }
};




const OrdersCell = ({ orderIds }) => {
  const ids = Array.isArray(orderIds)
    ? orderIds.filter((id) => id !== null && id !== undefined).map(String)
    : [];

  const total = ids.length;

  // === SIN ÓRDENES ===
  if (!total) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          px: 1.75,
          py: 0.5,
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)',
          width: 'fit-content',
          minWidth: 140,
          transition: 'all 0.2s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '4px',
            bgcolor: 'rgba(0, 0, 0, 0.04)',
          }}
        >
          <LinkOff sx={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.36)' }} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(0, 0, 0, 0.56)',
            fontWeight: 500,
            fontSize: '0.75rem',
            letterSpacing: '0.01em',
          }}
        >
          Sin orden asignada
        </Typography>
      </Box>
    );
  }

  // === CON ÓRDENES ===
  // Configuración: mostramos hasta 5 órdenes completas antes de ocultar
  const MAX_VISIBLE_IDS = 5;
  const SHOW_HIDDEN_FROM = 6; // A partir de 6 órdenes, mostramos contador
  
  if (total <= MAX_VISIBLE_IDS) {
    // Mostrar todas las órdenes (hasta 5)
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.625,
          borderRadius: '6px',
          border: '1px solid',
          borderColor: 'rgba(25, 118, 210, 0.12)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
          width: 'fit-content',
          boxShadow: '0 1px 3px rgba(25, 118, 210, 0.04)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: 3,
            height: '100%',
            background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
          }
        }}
      >
        {/* Icono con contador */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '4px',
            bgcolor: 'rgba(25, 118, 210, 0.06)',
            border: '1px solid rgba(25, 118, 210, 0.08)',
            flexShrink: 0,
          }}
        >
          <LocalShipping sx={{ fontSize: 13, color: '#1976d2' }} />
          {total > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid white',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.55rem',
                  lineHeight: 1,
                }}
              >
                {total}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Lista de IDs */}
        <Stack
          direction="row"
          spacing={0.375}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          {ids.map((id, index) => (
            <Box
              key={id}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 0.75,
                py: 0.125,
                borderRadius: '3px',
                background: index === 0 
                  ? 'rgba(25, 118, 210, 0.08)'
                  : 'rgba(66, 165, 245, 0.06)',
                border: `1px solid ${index === 0 ? 'rgba(25, 118, 210, 0.15)' : 'rgba(66, 165, 245, 0.1)'}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  fontFamily: "'Roboto Mono', monospace",
                  color: index === 0 ? '#1565c0' : '#42a5f5',
                  lineHeight: 1.2,
                }}
              >
                {id}
              </Typography>
              {index === 0 && total === 1 && (
                <Typography
                  component="span"
                  sx={{
                    ml: 0.375,
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    color: 'rgba(25, 118, 210, 0.6)',
                  }}
                >
                  OC
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  // === MÁS DE 5 ÓRDENES ===
  // Mostramos las primeras 3-4 y contador del resto
  const visibleIds = ids.slice(0, 4); // Mostramos 4 antes de poner contador
  const hiddenCount = total - visibleIds.length;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.625,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'rgba(25, 118, 210, 0.15)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
        width: 'fit-content',
        boxShadow: '0 1px 3px rgba(25, 118, 210, 0.06)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          height: '100%',
          background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
        }
      }}
    >
      {/* Icono con contador */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: '4px',
          bgcolor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.12)',
          flexShrink: 0,
        }}
      >
        <LocalShipping sx={{ fontSize: 13, color: '#1976d2' }} />
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid white',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '0.6rem',
              lineHeight: 1,
            }}
          >
            {total}
          </Typography>
        </Box>
      </Box>

      {/* IDs visibles */}
      <Stack
        direction="row"
        spacing={0.375}
        alignItems="center"
        sx={{
          minWidth: 0,
        }}
      >
        {visibleIds.map((id, index) => (
          <Box
            key={id}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 0.75,
              py: 0.125,
              borderRadius: '3px',
              background: index === 0 
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(66, 165, 245, 0.08)',
              border: `1px solid ${index === 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(66, 165, 245, 0.15)'}`,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                fontFamily: "'Roboto Mono', monospace",
                color: index === 0 ? '#1565c0' : '#42a5f5',
                lineHeight: 1.2,
              }}
            >
              {id}
            </Typography>
            {index === 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 0.375,
                  fontSize: '0.6rem',
                  fontWeight: 500,
                  color: 'rgba(25, 118, 210, 0.6)',
                }}
              >
                OC
              </Typography>
            )}
          </Box>
        ))}

        {/* Contador de órdenes ocultas */}
        <Tooltip 
          title={`${hiddenCount} orden${hiddenCount > 1 ? 'es' : ''} adicional${hiddenCount > 1 ? 'es' : ''}`}
          placement="top"
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 22,
              height: 22,
              borderRadius: '4px',
              bgcolor: 'rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              cursor: 'help',
              ml: 0.25,
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.06)',
                borderColor: 'rgba(0, 0, 0, 0.12)',
              }
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0, 0, 0, 0.56)',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            >
              +{hiddenCount}
            </Typography>
          </Box>
        </Tooltip>
      </Stack>
    </Box>
  );
};



// 1. Componente para ID de Factura
const InvoiceIDCell = ({ id }) => (
  <Typography
    variant="body2"
    sx={{
      fontWeight: 700,
      color: 'primary.main', // Resaltar IDs importantes
      borderBottom: '1px dotted',
      borderColor: 'primary.light',
      cursor: 'pointer' // Sugiere que es clickable/copiable
    }}
  >
    #{id}
  </Typography>
);

// 2. Componente para Días de Gracia (Añade contexto visual de tiempo)
const GraceDaysCell = ({ days }) => {
  let color = 'info.main';
  if (days === 0) color = 'warning.main';
  if (days < 0) color = 'error.main'; // Aunque GraceDays deberían ser >= 0, es buena práctica

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: `${color}10`, // Fondo muy sutil
      minWidth: 70
    }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: color }}>
        {`${days} días`}
      </Typography>
    </Box>
  );
};

// 3. Componente para Celdas Monetarias (Total, Abono, Saldo)
// Esto asegura un formateo consistente y limpio alineado a la derecha.
const MonetaryCell = ({ value, color = 'text.primary', formatFunc }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: color, fontSize: '0.875rem' }}>
      {formatFunc(Number(value || 0))}
    </Typography>
  </Box>
);

// Función de Formato de Moneda Centralizada (Quetzales)
const formatQuetzales = (value) =>
  new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2
  }).format(value);
// ===== Pill para el DataGrid =====
function MiniFilePill({ file, onOpen, onDelete }) {
  const isImg = isImageLike(file);
  const isPdf = isPdfLike(file);
  const url = getPreviewUrl(file);

  // cleanup del objectURL si es blob
  React.useEffect(() => {
    if (typeof file === 'string') return;
    const u = url;
    return () => u && u.startsWith('blob:') && URL.revokeObjectURL(u);
  }, [file, url]);

  const label = isImg ? 'Imagen' : isPdf ? 'PDF' : 'Archivo';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        border: '1px solid rgba(0,0,0,0.08)',
        px: 1.2,
        py: 0.6,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(16,23,60,.06), rgba(16,23,60,.02))',
        boxShadow: '0 4px 12px rgba(16,23,60,.08)',
        transition: 'transform .15s ease, box-shadow .2s ease',
        '&:hover': { boxShadow: '0 6px 16px rgba(16,23,60,.12)' },
      }}
    >
      <Tooltip title="Previsualizar">
        <IconButton
          size="small"
          onClick={onOpen}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.2,
            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            color: 'common.white',
            boxShadow: '0 4px 12px rgba(46,125,50,.25)',
            '&:hover': {
              backgroundPosition: 'right center',
              boxShadow: '0 6px 16px rgba(46,125,50,.35)',
            },
          }}
        >
          <Preview fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      {onDelete && (
        <Tooltip title="Eliminar documento">
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            sx={{ ml: 0.5, width: 28, height: 28, '&:hover': { backgroundColor: 'rgba(244,67,54,0.08)' } }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function FilePreviewDialog({ open, file, onClose }) {
  const url = file ? getPreviewUrl(file) : '';
  const isImg = isImageLike(file);
  const isPdf = isPdfLike(file);
  const name = typeof file === 'string' ? (file.split('/').pop() || 'Documento') : (file?.name || 'Documento');

  React.useEffect(() => {
    if (!file || typeof file === 'string') return;
    const u = url;
    return () => u && u.startsWith('blob:') && URL.revokeObjectURL(u);
  }, [file, url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={isImg ? 'md' : 'lg'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0px 10px 35px rgba(16,23,60,0.25)',
          overflow: 'hidden',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #f0f0f0', background: 'linear-gradient(135deg, #10173c 0%, #18224d 100%)', color: 'white' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {url && (
            <Button href={url} download variant="contained" size="small" sx={{ background: 'linear-gradient(135deg, #10173c 0%, #18224d 100%)', borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 2, py: 0.5, boxShadow: '0 4px 10px rgba(16,23,60,0.25)', '&:hover': { background: 'linear-gradient(135deg, #18224d 0%, #10173c 100%)', boxShadow: '0 6px 16px rgba(16,23,60,0.35)' } }}>Descargar</Button>
          )}
          <IconButton onClick={onClose} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <DialogContent dividers sx={{ p: 0, bgcolor: isImg ? '#0b1026' : '#fafafa', height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isImg && url && (
          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '0 0 12px 12px' }} />
        )}
        {isPdf && url && (
          <object data={url} type="application/pdf" width="100%" height="100%">
            <embed src={url} type="application/pdf" width="100%" height="100%" />
            <div style={{ padding: 24 }}>
              No se pudo mostrar el PDF.{' '}
              <a href={url} target="_blank" rel="noreferrer">Abrir en nueva pestaña</a>.
            </div>
          </object>
        )}
        {!isImg && !isPdf && (
          <div style={{ padding: 24 }}>
            No se puede previsualizar este tipo de archivo.{' '}
            {url && (<a href={url} target="_blank" rel="noreferrer">Abrir</a>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
const removeAccents = (str) => {
  if (!str) return '';
  // Usamos normalize('NFD') para descomponer los caracteres en su base + acento
  // y luego un regex para eliminar los acentos diacríticos.
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};
const typeToMeta = {
  // Las claves deben estar en minúsculas y sin acentos para la búsqueda.
  'factura cambiaria': {
    label: 'Factura Cambiaria',
    code: 'FC',
    icon: <Description fontSize="small" />,
    tone: { bg: 'rgba(37,99,235,0.08)', fg: '#1e3a8a', bd: 'rgba(37,99,235,0.25)' }
  },
  'factura electronica': { // Sin tilde en 'electrónica'
    label: 'Factura Electrónica',
    code: 'FE',
    icon: <InsertDriveFile fontSize="small" />,
    tone: { bg: 'rgba(14,165,233,0.10)', fg: '#0c4a6e', bd: 'rgba(14,165,233,0.25)' }
  },
  'vale': {
    label: 'Vale',
    code: 'V',
    icon: <ReceiptLong fontSize="small" />,
    tone: { bg: 'rgba(124,58,237,0.10)', fg: '#3b0764', bd: 'rgba(124,58,237,0.25)' }
  },
};


const initials = (fn = '', ln = '', un = '') => {
  const a = (fn || '').trim()[0] || '';
  const b = (ln || '').trim()[0] || '';
  const base = (a + b) || (un || '').trim()[0] || '?';
  return base.toUpperCase();
};


const InvoiceTypeCell = ({ typeString }) => {
  // 1. Normalizar la cadena de entrada (a minúsculas, sin tildes, sin espacios extra).
  const searchKey = removeAccents(String(typeString || '').toLowerCase().trim());

  // 2. Buscar en el mapa normalizado.
  const meta = typeToMeta[searchKey];

  // 3. Definir el estilo de Fallback (Estandar y Limpio)
  const defaultMeta = {
    label: typeString || 'Desconocido',
    code: 'N/A',
    icon: <InsertDriveFile fontSize="small" />,
    tone: { bg: 'rgba(150,150,150,0.1)', fg: '#505050', bd: 'rgba(150,150,150,0.5)' }
  };

  const finalMeta = meta || defaultMeta;

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      border: '1px solid',
      borderColor: finalMeta.tone.bd,
      bgcolor: finalMeta.tone.bg,
      color: finalMeta.tone.fg,
      px: 1,
      height: 26,
      borderRadius: 1.5
    }}>
      {finalMeta.icon}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {finalMeta.label}
      </Typography>
      {finalMeta.code !== 'N/A' && (
        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
          ({finalMeta.code})
        </Typography>
      )}
    </Box>
  );
};

const UserCell = ({ u }) => {
  const name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.username || '—';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
        {initials(u?.first_name, u?.last_name, u?.username)}
      </Avatar>
      <Typography variant="body2">{name}</Typography>
    </Box>
  );
};
const statusToFlags = {
  ICF: { confirm: true, sign: true },  // confirmado y firmado
  IFSC: { confirm: false, sign: true },  // firmado sin confirmar
  ISSF: { confirm: false, sign: false },  // sin confirmar ni firmar
};

// Meta global según combinación (etiqueta + tono)
const getGlobalMeta = (code) => {
  const s = statusToFlags[code] || { confirm: false, sign: false };
  if (s.confirm && s.sign) {
    return { label: 'Aprobada', color: 'success.main', Icon: CheckCircle };
  }
  if (!s.confirm && s.sign) {
    return { label: 'Firmada pendiente', color: 'info.main', Icon: InfoOutlined };
  }
  return { label: 'Pendiente', color: 'warning.main', Icon: WarningAmber };
};

const confirmedSourceMeta = {
  STEPPER: { label: 'Confirmada en Stepper', color: 'primary' },
  CONFIRM_MODAL: { label: 'Confirmada después', color: 'secondary' },
};

// Segmento pequeño y sobrio (Conf/Firma)
const Segment = ({ ok, label }) => (
  <Box
    sx={{
      height: 22,
      px: 0.75,
      borderRadius: 1,
      border: '1px solid',
      borderColor: (theme) => ok ? theme.palette.success.main : theme.palette.divider,
      bgcolor: (theme) => ok ? `${theme.palette.success.main}18` : 'background.paper',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      lineHeight: 1,
    }}
  >
    <Box
      sx={{
        width: 6, height: 6, borderRadius: '50%',
        bgcolor: (theme) => ok ? theme.palette.success.main : theme.palette.text.disabled,
      }}
    />
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: (theme) => ok ? theme.palette.success.main : theme.palette.text.secondary }}
    >
      {ok ? label : `Sin ${label.toLowerCase()}`}
    </Typography>
  </Box>
);
const InvoiceStatusEnterprise = ({ code, confirmedSource, lifecycleStatus }) => {
  const isVoided = String(lifecycleStatus || '').toUpperCase() === 'VOID';
  if (isVoided) {
    return (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
        <Chip
          size="small"
          color="error"
          variant="outlined"
          icon={<Cancel sx={{ fontSize: 16 }} />}
          label="Anulada"
          sx={{ fontWeight: 700 }}
        />
      </Box>
    );
  }
  const flags = statusToFlags[code] || { confirm: false, sign: false };
  const { label, color, Icon } = getGlobalMeta(code);
  const sourceMeta = confirmedSourceMeta[String(confirmedSource || '').toUpperCase()];

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          px: 1,
          py: 0.5,
          bgcolor: 'background.paper',
          width: '100%',
          maxWidth: 340, // ajusta si deseas
        }}
      >
        {/* Sello global compacto */}
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 120 }}>
          <Icon sx={{ fontSize: 18, color }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        </Stack>

        {/* Separador sutil */}
        <Box sx={{ flex: 1 }} />

        {/* Segmentos de estado (Conf / Firma) */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Tooltip title={flags.confirm ? 'Confirmado' : 'Sin confirmar'}>
            <span><Segment ok={flags.confirm} label="Confirmar" /></span>
          </Tooltip>
          <Tooltip title={flags.sign ? 'Firmado' : 'Sin firma'}>
            <span><Segment ok={flags.sign} label="Firma" /></span>
          </Tooltip>
        </Stack>
        {sourceMeta && (
          <Chip
            size="small"
            variant="outlined"
            color={sourceMeta.color}
            label={sourceMeta.label}
            sx={{ ml: 1 }}
          />
        )}
      </Box>
    </Box>
  );
};

const TotalCell = ({ value }) => {
  const n = Number(value ?? 0) || 0;
  const fmt = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(n);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.95rem',
          fontVariantNumeric: 'tabular-nums',
          color: 'text.primary', // más fuerte que 'secondary', pero no vibrante
        }}
      >
        {fmt}
      </Typography>
    </Box>
  );
};


const todayLocal = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

function AdjustmentModalContent({
  adjustmentInvoice,
  adjustmentValue,
  adjustmentError,
  adjustmentSaving,
  onChangeValue,
  onClose,
  onSave,
}) {
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Factura #{adjustmentInvoice?.id}
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Ajuste"
        value={adjustmentValue}
        onChange={(e) => onChangeValue(e.target.value)}
        inputProps={{ inputMode: "decimal" }}
        error={Boolean(adjustmentError)}
        helperText={adjustmentError || "Rango permitido: -100.00 a 100.00"}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button size="small" variant="outlined" onClick={onClose} disabled={adjustmentSaving}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSave}
          disabled={adjustmentSaving || adjustmentValue === '-' || adjustmentValue === '.' || adjustmentValue === '-.'}
        >
          {adjustmentSaving ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </Box>
  );
}

function InvoiceOrderLinksDialog({ open, invoice, onClose, onSaved }) {
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("current");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !invoice?.id) return;
    let active = true;
    setLoading(true);
    setError("");
    API.get(`invoice/${invoice.id}/order-links/`)
      .then(({ data }) => {
        if (!active) return;
        setOrders(Array.isArray(data?.orders) ? data.orders : []);
        setSelectedIds((data?.order_ids || []).map(Number));
      })
      .catch((requestError) => {
        if (active) {
          setError(extractApiError(requestError, "No se pudieron cargar las órdenes."));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, invoice?.id]);

  const visibleOrders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return orders.filter((order) => {
      if (statusFilter === "current" && order.historical) return false;
      if (statusFilter === "expired" && !order.historical) return false;
      if (!needle) return true;
      return [order.id, order.code].some((value) => (
        String(value || "").toLowerCase().includes(needle)
      ));
    });
  }, [orders, search, statusFilter]);

  const selectedPreview = useMemo(
    () => orders
      .filter((order) => selectedIds.includes(Number(order.id)))
      .reduce((summary, order) => ({
        products: summary.products + Number(order.preview?.products_reconciled || 0),
        bonuses: summary.bonuses + Number(order.preview?.bonuses_reconciled || 0),
        unmatched: summary.unmatched + Number(order.preview?.unmatched_lines || 0),
      }), { products: 0, bonuses: 0, unmatched: 0 }),
    [orders, selectedIds]
  );

  const toggleOrder = (order) => {
    const normalized = Number(order.id);
    setSelectedIds((current) => (
      current.includes(normalized)
        ? current.filter((id) => id !== normalized)
        : order.selectable === false
          ? current
          : [...current, normalized]
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await API.patch(`invoice/${invoice.id}/order-links/`, {
        order_ids: selectedIds,
      });
      await onSaved?.(data);
      onClose();
    } catch (requestError) {
      setError(extractApiError(
        requestError,
        "No se pudieron actualizar las órdenes."
      ));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vincular órdenes · Factura {invoice?.number || invoice?.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por número o código"
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_event, value) => value && setStatusFilter(value)}
          >
            <ToggleButton value="current">Vigentes</ToggleButton>
            <ToggleButton value="expired">Vencidas</ToggleButton>
            <ToggleButton value="all">Todas</ToggleButton>
          </ToggleButtonGroup>
          {error ? <Typography color="error" variant="body2">{String(error)}</Typography> : null}
          {loading ? (
            <Box sx={{ py: 5, display: "grid", placeItems: "center" }}><CircularProgress size={28} /></Box>
          ) : (
            <Stack spacing={0.75} sx={{ maxHeight: 390, overflowY: "auto" }}>
              {visibleOrders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No hay órdenes disponibles.</Typography>
              ) : visibleOrders.map((order) => {
                const checked = selectedIds.includes(Number(order.id));
                return (
                  <Paper
                    key={order.id}
                    variant="outlined"
                    onClick={() => toggleOrder(order)}
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      borderColor: checked ? "primary.main" : "divider",
                      bgcolor: checked ? "action.selected" : "background.paper",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        checked={checked}
                        size="small"
                        disabled={!checked && order.selectable === false}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2">OC {order.code}</Typography>
                          <Chip size="small" label={order.status_label || order.status} />
                          {order.historical ? <Chip size="small" color="warning" variant="outlined" label="Histórica" /> : null}
                          {order.currently_linked ? <Chip size="small" color="primary" variant="outlined" label="Vinculada" /> : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {order.date || "Sin fecha"} · {formatQuetzales(order.total || 0)}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography variant="caption">Productos: {order.preview?.products_reconciled || 0}</Typography>
                        <Typography variant="caption">Bonos: {order.preview?.bonuses_reconciled || 0}</Typography>
                        <Typography variant="caption" color={order.preview?.unmatched_lines ? "warning.main" : "text.secondary"}>
                          Sin coincidencia: {order.preview?.unmatched_lines || 0}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
          <Paper variant="outlined" sx={{ p: 1, bgcolor: "action.hover" }}>
            <Typography variant="subtitle2">Vista previa de la selección</Typography>
            <Typography variant="body2">
              {selectedIds.length} orden(es) · {selectedPreview.products} productos conciliados · {selectedPreview.bonuses} bonos conciliados · {selectedPreview.unmatched} líneas sin coincidencia
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Guardando..." : "Guardar vínculos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* --------------------- componente ---------------------- */
export default function ListInvoice() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const savedFilters = useSelector((state) => state.tabs.flows.listInvoice || null);
  const { isSuperuser, permissions } = useAuth();
  const canUseLogical = isSuperuser || hasAny(permissions, ["view_catalogs", "manage_catalogs"]);
  const canManageInvoice = isSuperuser || hasAny(permissions, ["manage_invoice"]);

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    startDate: todayLocal(),
    endDate: todayLocal(),
    status: "todas",
    payment: PAYMENT_FILTER_ALL,
    active: true,
    supplierId: null,
    dateType: "createdAt",
    overdue: "todas",
    lifecycle: "vigentes",
    paymentFlow: "todas",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const restoredFiltersRef = useRef(false);

  useEffect(() => {
    if (restoredFiltersRef.current) return;
    restoredFiltersRef.current = true;

    const parseDate = (v) => {
      if (!v) return null;
      if (v instanceof Date) return v;
      if (typeof v === "string") {
        const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
          const [_, Y, M, D] = m;
          return new Date(Number(Y), Number(M) - 1, Number(D)); // fecha local
        }
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    if (savedFilters && typeof savedFilters === "object") {
      const restoredStart = parseDate(savedFilters.startDate) || todayLocal();
      const restoredEnd = parseDate(savedFilters.endDate) || todayLocal(); // 👈 fallback a hoy
      const restoredSupplier = savedFilters.supplierId != null ? Number(savedFilters.supplierId) : null;

      setFilters((prev) => ({
        ...prev,
        ...savedFilters,
        startDate: restoredStart,
        endDate: restoredEnd,
        payment: normalizePaymentFilter(savedFilters.payment),
        supplierId: Number.isNaN(restoredSupplier) ? null : restoredSupplier,
      }));

      if (typeof savedFilters.search === "string") {
        setDebouncedSearch(savedFilters.search);
      }

      setEndDateTouched(!!savedFilters.endDate); // si no había endDate, lo consideramos "no tocado"
    } else {
      // Primera vez: sin filtros guardados -> usar HOY
      setFilters((prev) => ({ ...prev, startDate: todayLocal(), endDate: todayLocal() }));
    }
  }, [savedFilters]);

  // persist filters on change
  useEffect(() => {
    const serializable = {
      ...filters,
      startDate: filters.startDate ? toISODate(filters.startDate) : null,
      endDate: filters.endDate ? toISODate(filters.endDate) : null,
    };
    dispatch(mergeFlow({ name: 'listInvoice', data: serializable }));
  }, [dispatch, filters]);

  const uploadInputRef = React.useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [selectionModel, setSelectionModel] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [orderLinksOpen, setOrderLinksOpen] = useState(false);

  const [openNotes, setOpenNotes] = useState(false);
  const [notesList, setNotesList] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [appliedMap, setAppliedMap] = useState({});
  const [notesInvoice, setNotesInvoice] = useState(null);
  const [endDateTouched, setEndDateTouched] = useState(false);

  // Add receipt modal state
  const [addReceiptOpen, setAddReceiptOpen] = useState(false);
  const [addReceiptPayments, setAddReceiptPayments] = useState([]);
  const [bankFlowOpen, setBankFlowOpen] = useState(false);
  const [bankFlowInvoice, setBankFlowInvoice] = useState(null);
  const [bankFlowRequisition, setBankFlowRequisition] = useState(null);
  const [bankFlowObligation, setBankFlowObligation] = useState(null);
  const [bankFlowLoading, setBankFlowLoading] = useState(false);

  // Payment detail modal
  const [paymentDetailOpen, setPaymentDetailOpen] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [adjustmentInvoice, setAdjustmentInvoice] = useState(null);
  const [adjustmentValue, setAdjustmentValue] = useState("0");
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState("");

  const openFilePreview = (fileOrUrl) => {
    if (!fileOrUrl) return;
    setPreviewFile(fileOrUrl);
    setPreviewOpen(true);
  };

  const handleViewNotes = async (event, row) => {
    event.stopPropagation();
    try {
      setNotesInvoice(row || null);
      const res = await API.get('receiving-note/', { params: { invoice: row.id } });
      setNotesList(Array.isArray(res.data) ? res.data : []);
      setOpenNotes(true);
    } catch (err) {
      console.error('Error cargando notas vinculadas', err);
      setNotesList([]);
      setNotesInvoice(row || null);
      setOpenNotes(true);
    }
  };

  const handleUploadClick = (row) => {
    setUploadTargetId(row.id);
    uploadInputRef.current?.click();
  };



  const handleUploadChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f || !uploadTargetId) return;
    try {
      const fd = new FormData();
      fd.append("invoiceDocuments", f);
      await API.patch(`invoice/${uploadTargetId}/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchData();
    } catch (err) {
      console.error("Error subiendo documento de factura", err);
    } finally {
      e.target.value = "";
      setUploadTargetId(null);
    }
  };

  const isVoidedInvoice = useCallback((row) => {
    return String(row?.lifecycle_status || '').toUpperCase() === 'VOID';
  }, []);

  const handleEditInvoice = useCallback((row) => {
    if (isVoidedInvoice(row)) return;
    setSelectedInvoiceId(row.id);
    setOpenEdit(true);
  }, [isVoidedInvoice]);

  const handleOpenAdjustment = useCallback((row) => {
    const current = Number(row?.invoice_adjustment || 0);
    setAdjustmentInvoice(row || null);
    setAdjustmentValue(Number.isFinite(current) ? String(current) : "0");
    setAdjustmentError("");
    setAdjustmentOpen(true);
  }, []);

  const handleOpenBankFlow = useCallback(async (row) => {
    setBankFlowInvoice(row || null);
    setBankFlowRequisition(null);
    setBankFlowObligation(null);
    setBankFlowOpen(true);
    const requisitionId = typeof row?.bank_requisition === "object"
      ? row.bank_requisition?.id
      : (row?.bank_requisition || row?.bank_requisition_id);
    const obligationId = row?.abasto_obligation_id;
    if (!requisitionId && !obligationId) return;
    setBankFlowLoading(true);
    try {
      const requests = [];
      if (requisitionId) {
        requests.push(API.get(`bank-requisition/${requisitionId}/`).then(({ data }) => {
          setBankFlowRequisition(data || null);
        }));
      }
      if (obligationId) {
        requests.push(API.get(`abasto-obligations/${obligationId}/`).then(({ data }) => {
          setBankFlowObligation(data || null);
        }));
      }
      await Promise.all(requests);
    } catch (error) {
      console.error("Error cargando Abasto", error);
    } finally {
      setBankFlowLoading(false);
    }
  }, []);

  const openAbastoPage = useCallback(() => {
    dispatch(addTab({ label: "Abasto", path: "/abasto" }));
    navigate("/abasto");
  }, [dispatch, navigate]);

  const handleSelectionChange = useCallback((newSelection) => {
    setSelectionModel(newSelection);
    const selectedId = newSelection[0];
    const sel = invoices.find((inv) => inv.id === selectedId);
    setSelectedInvoice(sel || null);
  }, [invoices]);

  const voidInvoice = async (row) => {
    if (!row || isVoidedInvoice(row)) return;
    const confirmText = `¿Anular factura #${row.number || row.id}? Esta acción no elimina el registro y libera sus asignaciones.`;
    if (!window.confirm(confirmText)) return;
    const reason = String(window.prompt('Motivo de anulación:') || '').trim();
    if (!reason) {
      window.alert('Debes ingresar un motivo de anulación.');
      return;
    }
    try {
      await API.post(`invoice/${row.id}/void/`, { reason });
      await fetchData();
      setSelectionModel([]);
      setSelectedInvoice(null);
    } catch (e) {
      const message = e?.response?.data?.detail || e?.response?.data?.message || 'No se pudo anular la factura';
      window.alert(message);
      console.error("Error anulando factura", e);
    }
  };

  const loadSuppliers = useCallback(async () => {
    setSupplierLoading(true);
    try {
      const { data } = await API.get("invoice/suppliers/");
      setSupplierOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error obteniendo proveedores para filtro", err);
      setSupplierOptions([]);
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  useEffect(() => {
    if (!isSuperuser) {
      setCompanyOptions([]);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await RBAC.companies.list();
        if (!active) return;
        const items = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
        setCompanyOptions(items);
      } catch (err) {
        console.error("Error obteniendo empresas", err);
        if (active) setCompanyOptions([]);
      }
    })();
    return () => { active = false; };
  }, [isSuperuser]);

  const supplierLabelMap = useMemo(() => {
    const map = new Map();
    (supplierOptions || []).forEach((s) => {
      const id = Number(s?.id);
      if (!Number.isNaN(id)) {
        map.set(id, {
          legalName: s?.legalName || '',
          companyName: s?.companyName || '',
          label: s?.companyName || s?.legalName || `Proveedor ${id}`,
        });
      }
    });
    return map;
  }, [supplierOptions]);

  const companyNameMap = useMemo(() => {
    const map = new Map();
    (companyOptions || []).forEach((c) => {
      const id = Number(c?.id);
      if (!Number.isNaN(id)) {
        map.set(id, c?.name || c?.companyName || c?.legalName || `Empresa ${id}`);
      }
    });
    return map;
  }, [companyOptions]);

  const filteredInvoices = useMemo(() => {
    let base = invoices;
    if (filters.supplierId) {
      const sid = Number(filters.supplierId);
      base = base.filter((inv) => Number(inv.supplierId || inv.supplier_id) === sid);
    }
    if (filters.paymentFlow !== "todas") {
      base = base.filter((inv) => String(inv.payment_flow_type || "NORMAL") === filters.paymentFlow);
    }
    if (filters.overdue === "todas") return base;
    if (filters.overdue === "vencidas") return base.filter((inv) => inv.daysLeft < 0);
    if (filters.overdue === "no_vencidas") return base.filter((inv) => inv.daysLeft >= 0);
    return base;
  }, [invoices, filters.overdue, filters.paymentFlow, filters.supplierId]);

  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("es-GT", { style: "currency", currency: "GTQ" }).format(value || 0);
  }, []);



const getPaymentStatus = useCallback((status) => {
  const paymentMap = {
    PENDING: {
      label: "Pendiente",
      color: "#E64A19",
      accent: "#FF7043",
      icon: <AccessTime sx={{ fontSize: 12 }} />
    },
    PARTIAL: {
      label: "Parcial",
      color: "#1976D2",
      accent: "#42A5F5",
      icon: <TrendingUp sx={{ fontSize: 12 }} />
    },
    PAID: {
      label: "Pagada",
      color: "#388E3C",
      accent: "#66BB6A",
      icon: <CheckCircle sx={{ fontSize: 12 }} />
    },
    AWAITING_RECONCILIATION: {
      label: "Esperando conciliacion",
      color: "#512DA8",
      accent: "#9575CD",
      icon: <Receipt sx={{ fontSize: 12 }} />
    },
    VOID: {
      label: "Anulada",
      color: "#D32F2F",
      accent: "#EF5350",
      icon: <Cancel sx={{ fontSize: 12 }} />
    },
  };

  const item = paymentMap[normalizePaymentStatus(status, null)];
  if (!item) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.875,
        py: 0.375,
        borderRadius: '4px',
        backgroundColor: 'white',
        border: `1.5px solid ${item.color}`,
        minWidth: 95,
        height: 26,
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        cursor: 'pointer',
        boxShadow: `0 2px 4px ${item.color}15`,
        '&:hover': {
          backgroundColor: `${item.color}08`,
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 8px ${item.color}25, 0 1px 3px rgba(0, 0, 0, 0.1)`,
          '& .status-icon': {
            transform: 'scale(1.15)',
            color: item.accent,
          }
        }
      }}
    >
      {/* Barra lateral decorativa */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: `linear-gradient(180deg, ${item.color} 0%, ${item.accent} 100%)`,
        }}
      />
      
      {/* Icono */}
      <Box
        className="status-icon"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '3px',
          backgroundColor: `${item.color}15`,
          color: item.color,
          ml: 0.5,
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}
      >
        {item.icon}
      </Box>
      
      {/* Texto */}
      <Typography
        variant="caption"
        sx={{
          color: item.color,
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.01em',
          lineHeight: 1,
          flexGrow: 1,
          textAlign: 'center',
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
}, []);

  const getStatusLabel = (code) => ({ ICF: "Conf. y firm.", ISSF: "Sin conf. ni firm.", IFSC: "Firm. sin conf." }[code] || code);
  const getPaymentLabel = (code) => ({
    PENDING: "Pendiente",
    PARTIAL: "Parcial",
    AWAITING_RECONCILIATION: "Esperando conciliacion",
    PAID: "Pagada",
    VOID: "Anulada",
  }[code] || code);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      // Si el usuario toca fecha inicio y no ha fijado fecha fin, default fin = hoy
      if (name === "startDate") {
        return { ...prev, startDate: value, endDate: prev.endDate || todayLocal() };
      }
      if (name === "endDate") {
        setEndDateTouched(Boolean(value));
        return { ...prev, endDate: value };
      }
      if (name === "supplierId") {
        const parsed = value ? Number(value) : null;
        return { ...prev, supplierId: Number.isNaN(parsed) ? null : parsed };
      }
      return { ...prev, [name]: value };
    });
  };

  // ⚠️ FIX: función que faltaba en tu snippet original
  const openAddReceiptModal = useCallback(async () => {
    if (!selectedInvoice) return;
    try {
      // INTENTO 1: usar el mismo endpoint que ya utilizas para ver aplicaciones
      const { data } = await SettlementsAPI.applications(selectedInvoice.id);
      const payments = Array.isArray(data?.payments) ? data.payments : [];
      const activePayments = payments.filter((p) => p?.payment__is_active !== false);
      const mapped = activePayments.map((p) => ({
        id: p.payment_id,
        label: `Pago ${getMethodLabel(p['payment__paymentMethod']) || ''}${p['payment__document__number'] ? ' Doc ' + p['payment__document__number'] : ''}`.trim(),
        applied: Number(p.amount || 0),
      }));
      setAddReceiptPayments(mapped);
      setAddReceiptOpen(true);
    } catch (e) {
      console.error('No se pudieron cargar pagos vinculados a la factura', e);
      setAddReceiptPayments([]);
      setAddReceiptOpen(true); // aún así permite abrir para testear
    }
  }, [selectedInvoice]);


  const columns = useMemo(() => {
    const cols = [
    {
      field: "selection",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Checkbox
            size="small"
            checked={selectionModel.includes(params.row.id)}
            onChange={() => setSelectionModel([params.row.id])}
            inputProps={{ "aria-label": "select-row" }}
            sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 22 } }}
          />
        </Box>
      ),
    },
    {
      field: "is_active",
      headerName: "",
      width: 50,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (isVoidedInvoice(row)) {
          return (
            <Tooltip title="Factura anulada">
              <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%", alignItems: "center" }}>
                <Cancel sx={{ color: "error.main", fontSize: 18 }} />
              </Box>
            </Tooltip>
          );
        }
        const status = normalizePaymentStatus(row.paymentStatus, null);
        const daysLeft = row.daysLeft;

        if (status === "PAID") {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
              <Tooltip title="Factura pagada">
                <CheckCircle sx={{ color: "success.main", fontSize: 22 }} />
              </Tooltip>
            </Box>
          );
        }
        if (status === "AWAITING_RECONCILIATION") {
          return (
            <Tooltip title="Esperando conciliacion de ajustes">
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                <ErrorOutline sx={{ color: "warning.main", fontSize: 22 }} />
              </Box>
            </Tooltip>
          );
        }

        // Indicador de Vencimiento (Default)
        let color = "success.main";
        let tooltipTitle = "A tiempo";
        if (daysLeft <= 3 && daysLeft >= 0) {
          color = "warning.main";
          tooltipTitle = `Vence en ${daysLeft} días`;
        }
        if (daysLeft < 0) {
          color = "error.main";
          tooltipTitle = `Vencido hace ${Math.abs(daysLeft)} días`;
        }

        return (
          <Tooltip title={tooltipTitle}>
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%", alignItems: "center" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
            </Box>
          </Tooltip>
        );
      },
      sortable: false,
      filterable: false,
    },
    {
      field: 'archivo',
      headerName: 'Documento',
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const fileUrl = params.row.invoiceDocuments;
        if (!fileUrl) {
          return (
            <Tooltip title="Adjuntar documento">
              <IconButton size="small" onClick={() => handleUploadClick(params.row)}>
                <AttachFile fontSize="small" color="disabled" />
              </IconButton>
            </Tooltip>
          );
        }
        return (
          <MiniFilePill
            file={fileUrl}
            onOpen={() => openFilePreview(fileUrl)}
            onDelete={async (e) => {
              e.stopPropagation();
              if (window.confirm('¿Eliminar este documento de la factura?')) {
                try {
                  await API.patch(`invoice/${params.row.id}/`, { invoiceDocuments: null });
                  await fetchData();
                } catch (err) {
                  console.error('Error eliminando documento', err);
                }
              }
            }}
          />
        );
      },
    },
    {
      field: "id",
      headerName: "ID",
      width: 70,
      align: 'center',
      headerAlign: 'center',
      // --- Uso de Componente UI ---
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Box>
      )
    },
    {
      field: "supplier",
      headerName: "Proveedor",
      width: 200,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {value}
          </Typography>
        </Box>
      )
    },
    {
      field: "date",
      headerName: "Fecha Factura",
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatYMD(row?.date)}
          </Typography>
        </Box>
      )
    },
    {
      field: "number",
      headerName: "Número",
      width: 100,
      renderCell: ({ value }) => <InvoiceIDCell id={value} />
    },
    { field: "series", headerName: "Serie", width: 100 },
    {
      field: 'invoice_type',
      headerName: 'Tipo',
      width: 210,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <InvoiceTypeCell typeString={row?.invoice_type?.name} />
      ),
    },
    {
      field: "paymentType",
      headerName: "Tipo de Pago",
      width: 120,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {value}
          </Typography>
        </Box>
      )
    },
    {
      field: "paymentStatus",
      headerName: "Estado financiero",
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          {getPaymentStatus(params.value)}
        </Box>
      )
    },
    {
      field: "receipt_state",
      headerName: "Recibo",
      width: 145,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const receipt = row?.receipt_state || {};
        const colors = {
          NO_PAYMENT: 'default',
          MISSING: 'warning',
          PARTIAL: 'info',
          COMPLETE: 'success',
        };
        return (
          <Chip
            size="small"
            variant="outlined"
            color={colors[receipt.receipt_status] || 'default'}
            label={receipt.receipt_status_label || 'Sin informacion'}
          />
        );
      },
      sortable: false,
    },
    {
      field: "payment_flow_type",
      headerName: "Flujo",
      width: 220,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        if (!isBankRequisitionInvoice(row)) {
          return (
            <Typography variant="caption" color="text.secondary">
              Normal
            </Typography>
          );
        }
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ width: '100%', overflow: 'hidden' }}>
            <Chip
              size="small"
              icon={<AccountBalance sx={{ fontSize: 16 }} />}
              label="Abasto"
              variant="outlined"
              sx={{
                color: '#10173c',
                borderColor: 'rgba(16, 23, 60, 0.45)',
                bgcolor: 'rgba(16, 23, 60, 0.05)',
                '& .MuiChip-icon': { color: '#10173c' },
              }}
            />
            {(row.bank_payment_status || row.abasto_obligation_status_label) && (
              <Chip
                size="small"
                label={bankPaymentStatusLabels[row.bank_payment_status] || row.abasto_obligation_status_label}
                color={bankPaymentStatusColors[row.bank_payment_status] || abastoStatusColors[row.abasto_obligation_status] || 'default'}
                variant="outlined"
              />
            )}
          </Stack>
        );
      },
    },

    {
      field: "invoiceStatus",
      headerName: "Estado",
      width: 400,
      renderCell: ({ row, value }) => (
        <InvoiceStatusEnterprise
          code={String(value || '')}
          confirmedSource={row?.confirmed_source}
          lifecycleStatus={row?.lifecycle_status}
        />
      ),
    },

    {
      field: "graceDays", headerName: "T. Gracia", width: 110,
      align: 'center',
      headerAlign: 'center',
      // --- Uso de Componente UI ---
      renderCell: (params) => <GraceDaysCell days={params.value} />
    },
    {
      field: "invoice_adjustment",
      headerName: "Ajuste manual",
      width: 120,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => <MonetaryCell value={row?.invoice_adjustment ?? 0} formatFunc={formatQuetzales} color="warning.dark" />,
    },
    {
      field: "product_discount_amount",
      headerName: "Producto descontado",
      width: 170,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => (
        <MonetaryCell
          value={row?.product_discount_amount ?? 0}
          formatFunc={formatQuetzales}
          color="error.dark"
        />
      ),
    },
    {
      field: "total",
      headerName: "Total",
      width: 140,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => <TotalCell value={row?.rawTotal ?? row?.total} />,
    },
    {
      field: "abono",
      headerName: "Abonado",
      width: 130,
      headerAlign: 'right',
      align: 'right',
      // --- Uso de Componente UI ---
      renderCell: ({ row }) => <MonetaryCell value={row?.abono} formatFunc={formatQuetzales} color="success.dark" />
    },

    {
      field: "saldo",
      headerName: "Saldo",
      width: 130,
      headerAlign: 'right',
      align: 'right',
      // --- Uso de Componente UI ---
      renderCell: ({ row }) => <MonetaryCell value={row?.saldo} formatFunc={formatQuetzales} color="success" />
    },
    {
      field: "orders",
      headerName: "Órdenes",
      width: 170,
      sortable: false,
      filterable: false,
      align: 'left',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <OrdersCell orderIds={row.order_ids} />
      ),
    },

    {
      field: "hecho_por",
      headerName: "Hecho por",
      width: 200,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <UserCell u={row?.createdBy} />
        </Box>
      )
    },
...(isSuperuser ? [
  {
    field: "company",
    headerName: "Empresa",
    width: 200,
    renderCell: ({ row }) => {
      const companyId = row.company?.id || row.company_id || (typeof row.company === 'number' ? row.company : null);
      const resolvedName =
        row.company?.name ||
        row.company_name ||
        row.companyName ||
        (Number.isFinite(Number(companyId)) ? companyNameMap.get(Number(companyId)) : null) ||
        (typeof row.company === 'string' ? row.company : null) ||
        "-";
      
      const hasCompany = resolvedName !== '-';
      
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 1.5,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              py: 0.75,
              px: 1,
              borderRadius: '6px',
              background: hasCompany 
                ? 'linear-gradient(135deg, rgba(6, 95, 70, 0.04) 0%, rgba(16, 185, 129, 0.02) 100%)'
                : 'transparent',
              border: `1px solid ${hasCompany ? 'rgba(6, 95, 70, 0.1)' : 'transparent'}`,
              overflow: 'hidden',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': hasCompany && {
                background: 'linear-gradient(135deg, rgba(6, 95, 70, 0.07) 0%, rgba(16, 185, 129, 0.04) 100%)',
                borderColor: 'rgba(6, 95, 70, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(6, 95, 70, 0.1)',
              },
              '&::before': hasCompany && {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: 'linear-gradient(180deg, #065f46 0%, #10b981 100%)',
              }
            }}
          >
            {/* Contenido */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
              {hasCompany && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '5px',
                    background: 'linear-gradient(135deg, #10173c 0%, #1e2a5a 100%)',
                    color: 'white',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(6, 95, 70, 0.2)',
                  }}
                >
                  <Business sx={{ fontSize: 13 }} />
                </Box>
              )}
              
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: hasCompany ? 700 : 400,
                    fontSize: '0.85rem',
                    color: hasCompany ? ' #10173c' : '#1e2a5a',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontFamily: hasCompany ? "'Inter', system-ui, sans-serif" : 'inherit',
                    letterSpacing: hasCompany ? '0.01em' : 'normal',
                  }}
                >
                  {resolvedName}
                </Typography>
                
                {/* ID sutil debajo */}
                {Number.isFinite(Number(companyId)) && hasCompany && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(6, 95, 70, 0.5)',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      display: 'block',
                      letterSpacing: '0.03em',
                    }}
                  >
                    Empresa #{companyId}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }
  },
] : []),
    {
      field: "createdAt",
      headerName: "Creado",
      width: 130,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            {formatYMDHMS(row?.createdAt)}
          </Typography>
        </Box>
      )

    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 190,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const voided = isVoidedInvoice(row);
        return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          {isBankRequisitionInvoice(row) && (
            <Tooltip title={row?.abasto_obligation_id ? "Ver Abasto" : "Crear/ver obligacion"}>
              <IconButton
                size="small"
                color="info"
                onClick={() => {
                  if (row?.abasto_obligation_id) {
                    handleOpenBankFlow(row);
                  } else {
                    openAbastoPage();
                  }
                }}
              >
                <AccountBalance fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Ver notas de entrada">
            <IconButton color="secondary" onClick={(e) => handleViewNotes(e, row)}>
              <NoteAlt />
            </IconButton>
          </Tooltip>
          <Tooltip title={voided ? "Factura anulada (solo lectura)" : "Editar"}>
            <span>
            <IconButton color="primary" onClick={() => handleEditInvoice(row)} disabled={voided}>
              <Edit />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={voided ? "Factura anulada" : "Ajuste"}>
            <span>
            <IconButton size="small" color="warning" onClick={() => handleOpenAdjustment(row)} disabled={voided}>
              <TrendingUp fontSize="small" />
            </IconButton>
            </span>
          </Tooltip>
          {canManageInvoice && !voided && row?.is_active && (
            <Tooltip title="Anular factura">
              <IconButton size="small" color="error" onClick={() => voidInvoice(row)}>
                <Cancel fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={['PARTIAL', 'AWAITING_RECONCILIATION', 'PAID'].includes(normalizePaymentStatus(row.paymentStatus, null)) ? 'Ver aplicaciones' : 'Factura sin aplicaciones'}>
            <span>
              <IconButton size="small" disabled={voided || !['PARTIAL', 'AWAITING_RECONCILIATION', 'PAID'].includes(normalizePaymentStatus(row.paymentStatus, null))} onClick={async () => {
                const { data } = await SettlementsAPI.applications(row.id);
                setPaymentDetail(data);
                setPaymentDetailOpen(true);
              }}>
                <ReceiptLong fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )},
    },
    ];

    return cols;
  }, [getPaymentStatus, selectionModel, isSuperuser, companyNameMap, handleOpenAdjustment, handleEditInvoice, handleOpenBankFlow, openAbastoPage, isVoidedInvoice, voidInvoice, canManageInvoice]);

  const deleteInvoice = async (row) => {
    try {
      await API.patch(`invoice/${row.id}/`, { is_active: false });
      await fetchData();
      setSelectionModel([]);
      setSelectedInvoice(null);
    } catch (e) {
      console.error("Error eliminando factura", e);
    }
  };

  const restoreInvoice = async (row) => {
    try {
      await API.patch(`invoice/${row.id}/`, { is_active: true });
      await fetchData();
      setSelectionModel([]);
      setSelectedInvoice(null);
    } catch (e) {
      console.error("Error restaurando factura", e);
    }
  };

  const deleteInvoicePermanent = async (row) => {
    try {
      if (window.confirm("¿Eliminar permanentemente esta factura? Esta acción no se puede deshacer")) {
        await API.delete(`invoice/${row.id}/`);
        await fetchData();
        setSelectionModel([]);
        setSelectedInvoice(null);
      }
    } catch (e) {
      console.error("Error eliminando permanentemente factura", e);
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        status: filters.status !== "todas" ? filters.status : undefined,
        payment_status: filters.payment !== "todas" ? filters.payment : undefined,
        active: filters.active,
        date_field: filters.dateType || "date",
        supplier: filters.supplierId || undefined,
        payment_flow_type: filters.paymentFlow !== "todas" ? filters.paymentFlow : undefined,
        lifecycle_status: filters.lifecycle === "anuladas"
          ? "VOID"
          : (filters.lifecycle === "vigentes" ? "ACTIVE" : undefined),
      };
      // Normalizar rango de fechas:
      // - Si no hay fechas -> usar hoy en ambos extremos.
      // - Si hay solo start -> end = hoy (mientras usuario no toque fin).
      // - Si hay solo end -> start = end (misma fecha).
      let start = filters.startDate;
      let end = filters.endDate;
      const todayIso = dayjs().format("YYYY-MM-DD");

      if (!start && !end) {
        start = todayIso;
        end = todayIso;
      } else {
        start = start ? toISODate(start) : null;
        end = end ? toISODate(end) : null;
        if (start && (!end || !endDateTouched)) end = todayIso;
        if (!start && end) start = end; // evitar rango abierto inverso
      }

      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const response = await API.get("invoice/", { params });
      const today = dayjs();
      const formatted = response.data.map((inv) => {
        const createdDate = dayjs(inv.createdAt);
        const graceDays = today.diff(createdDate, "day");
        const creditPeriod = inv.supplier?.creditPeriod || 0;
        const daysLeft = creditPeriod - graceDays;
        const rawBaseTotal = Number(inv.total) || 0;
        const invoiceAdjustment = Number(inv.invoice_adjustment || 0) || 0;
        const subtotalBaseApi = Number(inv.subtotal_base);
        const totalEffectiveApi = Number(inv.total_effective);
        const rawTotal = Number(
          (
            Number.isFinite(totalEffectiveApi)
              ? totalEffectiveApi
              : (Number.isFinite(subtotalBaseApi) ? subtotalBaseApi : rawBaseTotal)
          ).toFixed(2)
        );
        const abono = Number(inv.abono || 0) || 0;
        const saldo = Number(inv.saldo || (rawTotal - abono)) || 0;
        const productDiscountAmount = Number(inv.product_discount_amount || 0) || 0;
        return normalizeInvoicePaymentStatus({
          id: inv.id,
          ...inv,
          supplier: inv.supplier?.legalName || "Desconocido",
          supplierId: inv.supplier_id || inv.supplier?.id || null,
          total: formatCurrency(rawTotal),
          rawBaseTotal,
          rawTotal,
          invoice_adjustment: invoiceAdjustment,
          product_discount_amount: productDiscountAmount,
          abono,
          saldo,
          graceDays,
          daysLeft,
          hasDoc: !!inv.invoiceDocuments,
          ...(isSuperuser ? {} : {
            company: null,
            company_id: null,
            company_name: null,
            companyName: null,
          }),
        });
      });
      setInvoices(formatted);
      if (formatted.length > 250) {
        // Evitamos spamear el backend con miles de llamadas cuando el listado es grande.
        setAppliedMap({});
      } else {
        try {
          const ids = formatted.map(f => f.id);
          const results = await Promise.allSettled(ids.map(async (id) => {
            const { data } = await SettlementsAPI.applications(id);
            const pay = Array.isArray(data?.payments) ? data.payments : [];
            const cred = Array.isArray(data?.credits) ? data.credits : [];
            const sumPay = pay
              .filter((p) => p?.payment__is_active !== false)
              .reduce((s, p) => s + (Number(p.amount) || 0), 0);
            const sumCred = cred.reduce((s, c) => s + (Number(c.amount) || 0), 0);
            const applied = Number((sumPay + sumCred).toFixed(2));
            return { id, applied };
          }));
          const next = {};
          for (const r of results) {
            if (r.status === 'fulfilled' && r.value) {
              const { id, applied } = r.value;
              const inv = formatted.find(x => x.id === id);
              const rawTotal = inv?.rawTotal || Number(inv?.total) || 0;
              const remaining = Math.max(0, Number((rawTotal - applied).toFixed(2)));
              next[id] = { applied, remaining };
            }
          }
          setAppliedMap(next);
        } catch (e) {
          console.warn('No se pudo calcular abonados para todas las facturas', e);
          setAppliedMap({});
        }
      }
      console.log("Facturas obtenidas:", formatted);
    } catch (error) {
      console.error("Error obteniendo facturas", error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, filters.startDate, filters.endDate, filters.status, filters.payment, filters.active, filters.dateType, filters.supplierId, filters.paymentFlow, filters.lifecycle, endDateTouched, formatCurrency, isSuperuser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { const h = setTimeout(() => setDebouncedSearch(filters.search), 500); return () => clearTimeout(h); }, [filters.search]);
  useEffect(() => { dispatch(addTab({ label: "Facturas", path: "/invoice/all-invoices" })); }, [dispatch]);

  const handleChangeAdjustmentValue = useCallback((value) => {
    if (value === '' || /^-?\d*\.?\d{0,2}$/.test(value)) {
      setAdjustmentValue(value);
      setAdjustmentError("");
    }
  }, []);

  const handleSaveAdjustment = useCallback(async () => {
    if (!adjustmentInvoice?.id) return;
    const parsed = adjustmentValue === '' ? 0 : Number(adjustmentValue);
    if (!Number.isFinite(parsed)) {
      setAdjustmentError("Ingresa un valor válido.");
      return;
    }
    const rounded = Math.round(parsed * 100) / 100;
    if (Math.abs(rounded) > 100) {
      setAdjustmentError("El ajuste no puede exceder 100.00");
      return;
    }
    setAdjustmentSaving(true);
    try {
      await API.patch(`invoice/${adjustmentInvoice.id}/`, { invoice_adjustment: rounded.toFixed(2) });
      setAdjustmentOpen(false);
      setAdjustmentInvoice(null);
      await fetchData();
    } catch (error) {
      setAdjustmentError("No se pudo guardar el ajuste.");
      console.error("Error guardando ajuste de factura", error);
    } finally {
      setAdjustmentSaving(false);
    }
  }, [adjustmentInvoice?.id, adjustmentValue, fetchData]);

  const totalFiltrado = useMemo(() => filteredInvoices.reduce((acc, i) => acc + (i.rawTotal || 0), 0), [filteredInvoices]);
  const totalSeleccion = useMemo(() => {
    const selIds = new Set(selectionModel);
    return filteredInvoices.filter((i) => selIds.has(i.id)).reduce((acc, i) => acc + (i.rawTotal || 0), 0);
  }, [filteredInvoices, selectionModel]);

  return (
    <>
      <FilePreviewDialog open={previewOpen} file={previewFile} onClose={() => { setPreviewOpen(false); setPreviewFile(null); }} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 2 }}>
        <MainText title="Facturas" subtitle="Listado de facturas" icon={Summarize} width="auto" />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {filters.status !== "todas" && (
            <Chip
              label={`Estado: ${getStatusLabel(filters.status)}`}
              onDelete={() => handleFilterChange("status", "todas")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.payment !== "todas" && (
            <Chip
              label={`Pago: ${getPaymentLabel(filters.payment)}`}
              onDelete={() => handleFilterChange("payment", "todas")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.paymentFlow !== "todas" && (
            <Chip
              label={filters.paymentFlow === BANK_REQUISITION_FLOW ? "Flujo: Abasto" : "Flujo: Normal"}
              onDelete={() => handleFilterChange("paymentFlow", "todas")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {/* Chip de Proveedor con efecto 3D sutil */}

          {filters.overdue !== "todas" && (
            <Chip
              label={`Vencidas: ${filters.overdue === "vencidas" ? "Sí" : "No"}`}
              onDelete={() => handleFilterChange("overdue", "todas")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.lifecycle !== "todas" && (
            <Chip
              label={`Vigencia: ${filters.lifecycle === "anuladas" ? "Anuladas" : "Vigentes"}`}
              onDelete={() => handleFilterChange("lifecycle", "todas")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.search && (
            <Chip
              label={`Búsqueda: "${filters.search}"`}
              onDelete={() => handleFilterChange("search", "")}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.startDate && (
            <Chip
              label={`Desde: ${new Date(filters.startDate).toLocaleDateString()}`}
              onDelete={() => handleFilterChange("startDate", null)}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.endDate && (
            <Chip
              label={`Hasta: ${new Date(filters.endDate).toLocaleDateString()}`}
              onDelete={() => handleFilterChange("endDate", null)}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          {filters.supplierId && (
            <Tooltip title={(supplierLabelMap.get(Number(filters.supplierId)) || {}).legalName || "Proveedor"}>
              <Chip
                label={`Proveedor: ${(supplierLabelMap.get(Number(filters.supplierId)) || {}).companyName || (supplierLabelMap.get(Number(filters.supplierId)) || {}).label || "Seleccionado"}`}
                onDelete={() => handleFilterChange("supplierId", null)}
                size="medium"
                sx={{
                  height: 32,
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  fontWeight: 500,
                  border: '1px solid #ddd',
                  boxShadow: 'none',
                  '& .MuiChip-deleteIcon': {
                    color: '#666',
                    fontSize: 18,
                    '&:hover': {
                      color: '#333',
                      backgroundColor: 'rgba(0,0,0,0.08)',
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              />
            </Tooltip>
          )}
          {filters.dateType && (
            <Chip
              label={filters.dateType === "date" ? "Fecha de factura" : "Fecha de creación"}
              color="primary"
              size="medium"
              sx={{ height: 32 }}
            />
          )}

          {!filters.startDate && !filters.endDate && (
            <Chip
              label="Mostrando solo hoy"
              color="info"
              size="medium"
              sx={{ height: 32 }}
            />
          )}

          {!filters.active && (
            <Chip
              label="Eliminados"
              color="error"
              onDelete={() => handleFilterChange("active", true)}
              size="medium"
              sx={{
                height: 32,
                '& .MuiChip-deleteIcon': { fontSize: 18 }
              }}
            />
          )}

          <CleanningButton onClick={() => {
            setEndDateTouched(false);
            const today = todayLocal();
            setFilters({
              search: "",
              startDate: today,
              endDate: today,
              status: "todas",
              payment: "todas",
              active: true,
              supplierId: null,
              dateType: "date",
              overdue: "todas",
              lifecycle: "vigentes",
              paymentFlow: "todas"
            });
          }} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", justifyContent: "flex-start", mr: "30px" }}>
          <DatePick
            label="Fecha inicio"
            size="small"
            value={filters.startDate}
            onChange={(date) => {
              setEndDateTouched(false);
              handleFilterChange("startDate", date);
            }}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={200}
          />
          <DatePick
            label="Fecha fin"
            size="small"
            value={filters.endDate}
            onChange={(date) => {
              setEndDateTouched(true);
              handleFilterChange("endDate", date);
            }}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={200}
          />

          <FormControl sx={{ minWidth: 160, p: 0 }}>
            <InputLabel>Estado doc.</InputLabel>
            <Select size="small" value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)} label="Estado doc.">
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="ICF">Conf. y firm.</MenuItem>
              <MenuItem value="ISSF">Sin conf. ni firm.</MenuItem>
              <MenuItem value="IFSC">Firm. sin conf.</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, p: 0 }}>
            <InputLabel>Vencidas</InputLabel>
            <Select size="small" value={filters.overdue} onChange={(e) => handleFilterChange("overdue", e.target.value)} label="Vencidas">
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="vencidas">Sí</MenuItem>
              <MenuItem value="no_vencidas">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 145, p: 0 }}>
            <InputLabel>Vigencia</InputLabel>
            <Select size="small" value={filters.lifecycle} onChange={(e) => handleFilterChange("lifecycle", e.target.value)} label="Vigencia">
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="vigentes">Vigentes</MenuItem>
              <MenuItem value="anuladas">Anuladas</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150, p: 0 }}>
            <InputLabel>Pago</InputLabel>
            <Select size="small" value={filters.payment} onChange={(e) => handleFilterChange("payment", e.target.value)} label="Pago">
              <MenuItem value="todas">Todas</MenuItem>
              <MenuItem value="PENDING">Pendiente</MenuItem>
              <MenuItem value="PARTIAL">Parcial</MenuItem>
              <MenuItem value="AWAITING_RECONCILIATION">Esperando conciliacion</MenuItem>
              <MenuItem value="PAID">Pagada</MenuItem>
              <MenuItem value="VOID">Anulada</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 170, p: 0 }}>
            <InputLabel>Flujo</InputLabel>
            <Select size="small" value={filters.paymentFlow} onChange={(e) => handleFilterChange("paymentFlow", e.target.value)} label="Flujo">
              <MenuItem value="todas">Todos</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value={BANK_REQUISITION_FLOW}>Abasto</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220, p: 0, '& .MuiInputLabel-root': { transform: 'translate(14px, 9px) scale(1)' } }} size="small">
            <InputLabel
              id="supplier-select-label"
              shrink
              sx={{
                '&.Mui-focused, &.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75) !important',
                }
              }}
            >
              Proveedor
            </InputLabel>
<Select
              labelId="supplier-select-label"
              label="Proveedor"
              value={filters.supplierId === null ? "" : filters.supplierId}
              onChange={(e) => handleFilterChange("supplierId", e.target.value === "" ? null : e.target.value)}
              disabled={supplierLoading}
              // Esto a veces ayuda con el label
              notched
              displayEmpty
              renderValue={(selected) => {
                if (selected === "") return "Todos";
                const sup = supplierLabelMap.get(Number(selected));
                return (sup && sup.companyName) || (sup && sup.label) || "Proveedor";
              }}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {supplierOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.companyName || `Proveedor ${s.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "none" }}>
            <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>Vencidas</Box>
            <ToggleButtonGroup size="small" exclusive value={filters.overdue} onChange={(e, value) => handleFilterChange("overdue", value || "todas")} aria-label="Filtro de vencidas">
              <ToggleButton value="todas" aria-label="Todas"><FiberManualRecord sx={{ fontSize: 12, color: "grey.500", mr: 0.5 }} />Todas</ToggleButton>
              <ToggleButton value="vencidas" aria-label="Vencidas"><FiberManualRecord sx={{ fontSize: 12, color: "error.main", mr: 0.5 }} />Sí</ToggleButton>
              <ToggleButton value="no_vencidas" aria-label="No vencidas"><FiberManualRecord sx={{ fontSize: 12, color: "success.main", mr: 0.5 }} />No</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            variant="outlined"
            size="small"
            sx={{ p: 0, width: "auto" }}
            placeholder="Buscar..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }}
          />

          <Tooltip title={`Filtrar por ${filters.dateType === "createdAt" ? "fecha de factura" : "fecha de creación"}`}>
            <IconButton size="medium" onClick={() => handleFilterChange("dateType", filters.dateType === "createdAt" ? "date" : "createdAt")} sx={{ color: "text.secondary" }}>
              {filters.dateType === "createdAt" ? (<Event fontSize="small" />) : (<AccessTime fontSize="small" />)}
            </IconButton>
          </Tooltip>

          <CustomSwitch
            name="active"
            label={filters.active ? "Activo" : "Eliminado"}
            onChange={(name, value) => setFilters((prev) => ({ ...prev, [name]: value }))}
            initialValue={filters.active}
          />
          <Tooltip title="Refrescar">
            <IconButton size="small" onClick={fetchData} sx={{ color: "text.secondary" }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Botonera contextual de fila seleccionada */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {canManageInvoice && (
              <Tooltip title="Vincular órdenes">
                <span>
                  <IconButton
                    color="primary"
                    sx={{ border: "1px solid" }}
                    disabled={!selectedInvoice || !selectedInvoice.is_active || isVoidedInvoice(selectedInvoice)}
                    onClick={() => setOrderLinksOpen(true)}
                  >
                    <AddLink />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canManageInvoice && selectedInvoice && selectedInvoice.is_active && !isVoidedInvoice(selectedInvoice) && (
              <Tooltip title="Eliminar factura">
                <IconButton color="error" sx={{ border: "1px solid" }} onClick={() => deleteInvoice(selectedInvoice)}>
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
            {canManageInvoice && selectedInvoice && selectedInvoice.is_active && !isVoidedInvoice(selectedInvoice) && (
              <Tooltip title="Anular factura">
                <IconButton color="warning" sx={{ border: "1px solid" }} onClick={() => voidInvoice(selectedInvoice)}>
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}
            {selectedInvoice && !selectedInvoice.is_active && (
              <>
                <Tooltip title="Restaurar factura">
                  <IconButton color="success" sx={{ border: "1px solid" }} onClick={() => restoreInvoice(selectedInvoice)}>
                    <FiberManualRecord sx={{ color: "success.main" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar permanentemente">
                  <IconButton color="error" sx={{ border: "1px solid" }} onClick={() => deleteInvoicePermanent(selectedInvoice)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {selectedInvoice && !isBankRequisitionInvoice(selectedInvoice) && !isVoidedInvoice(selectedInvoice) && ['MISSING', 'PARTIAL'].includes(selectedInvoice?.receipt_state?.receipt_status) && (
              <Tooltip title="Agregar recibo a pago">
                <IconButton color="primary" sx={{ border: "1px solid" }} onClick={openAddReceiptModal}>
                  <ReceiptLong />
                </IconButton>
              </Tooltip>
            )}
            {selectedInvoice && isBankRequisitionInvoice(selectedInvoice) && !isVoidedInvoice(selectedInvoice) && (
              <Tooltip title={selectedInvoice?.abasto_obligation_id ? "Ver Abasto" : "Crear/ver obligacion"}>
                <IconButton
                  color="info"
                  sx={{ border: "1px solid" }}
                  onClick={() => {
                    if (selectedInvoice?.abasto_obligation_id) {
                      handleOpenBankFlow(selectedInvoice);
                    } else {
                      openAbastoPage();
                    }
                  }}
                >
                  <AccountBalance />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ height: 500, width: "100%", mt: 1, maxWidth: '100%', overflowX: 'hidden' }}>
        <input ref={uploadInputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleUploadChange} />
        <DataGrid
          rows={filteredInvoices}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[5, 10, 20]}
          pagination
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectionModel}
          disableColumnFilter
          disableColumnMenu
          rowHeight={50}
          columnHeaderHeight={50}
          slots={{ footer: GridFooterSummary }}
          slotProps={{ footer: { totalFiltrado, totalSeleccion, count: filteredInvoices.length } }}
          sx={{
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#10173c", color: "#ffffff" },
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#10173c !important", '&:hover, &:focus, &:active': { backgroundColor: "#10173c !important" } },
            "& .MuiDataGrid-columnHeaderTitle": { color: "#ffffff" },
            "& .MuiDataGrid-iconButtonContainer": { color: "#ffffff !important" },
            "& .MuiDataGrid-menuIcon": { color: "#ffffff !important" },
            "& .MuiDataGrid-columnSeparator": { color: "#ffffff !important", '&:hover': { color: "#ffffff !important" } },
            "& .MuiDataGrid-cell": { padding: "0 8px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center" },
            "& .MuiDataGrid-row": {
              '&:hover': { cursor: "pointer", backgroundColor: "rgba(16, 23, 60, 0.08)" },
              '&.Mui-selected': { backgroundColor: "rgba(16, 23, 60, 0.12)", '&:hover': { backgroundColor: "rgba(16, 23, 60, 0.15)" } },
            },
          }}
        />

        <CustomModal
          open={adjustmentOpen}
          handleClose={() => setAdjustmentOpen(false)}
          setOpen={setAdjustmentOpen}
          title="Ajuste de factura"
          width="360px"
          component={AdjustmentModalContent}
          componentProps={{
            adjustmentInvoice,
            adjustmentValue,
            adjustmentError,
            adjustmentSaving,
            onChangeValue: handleChangeAdjustmentValue,
            onClose: () => setAdjustmentOpen(false),
            onSave: handleSaveAdjustment,
          }}
        />

        {/* Notas vinculadas */}
        <CustomModal
          open={openNotes}
          handleClose={() => setOpenNotes(false)}
          setOpen={setOpenNotes}
          title="Notas vinculadas"
          icon={NoteAlt}
          width="40%"
          component={() => (
            <Box sx={{ p: 2 }}>
              {notesInvoice && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Firmo:</Typography>{' '}
                  <Typography variant="body2" component="span">
                    {(() => {
                      const u = notesInvoice.signedBy || {};
                      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || '';
                      return name || '-';
                    })()}
                  </Typography>
                </Box>
              )}
              {!notesList?.length ? (
                <Typography variant="body2">Sin notas vinculadas</Typography>
              ) : (
                notesList.map((r) => {
                  const n = r.reNote_obj || {};
                  const fmt = (v) => (v ? new Date(v).toLocaleDateString("es-GT", { year: "numeric", month: "short", day: "2-digit" }) : "—");
                  const logicalName = n.logical_warehouse?.name || '';
                  const legacyText = n.legacy_warehouse_text || '';
                  const warehouseLabel = (() => {
                    if (!canUseLogical) {
                      return 'No aplica';
                    }
                    if (logicalName) {
                      return logicalName;
                    }
                    if (legacyText || n.warehouse) {
                      const text = legacyText || n.warehouse;
                      return `Legacy: ${text}`;
                    }
                    return '-';
                  })();
                  return (
                    <Paper key={r.id} elevation={0} sx={{ mb: 1, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, '&:hover': { boxShadow: 2, borderColor: "action.focus" } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Nota de recepción #{n.number ?? "—"}</Typography>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: n.is_active ? "success.main" : "text.disabled" }} />
                          <Typography variant="caption" sx={{ color: n.is_active ? "success.main" : "text.secondary" }}>{n.is_active ? "Activa" : "Inactiva"}</Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>Bodega</Typography>
                          <Typography variant="body2">{warehouseLabel}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>Fecha nota</Typography>
                          <Typography variant="body2">{fmt(n.date)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>Creada</Typography>
                          <Typography variant="body2">{fmt(n.createdAt)}</Typography>
                        </Grid>
                      </Grid>
                      {r.comment && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>Comentario</Typography>
                          <Typography variant="body2">{r.comment}</Typography>
                        </>
                      )}
                    </Paper>
                  );
                })
              )}
            </Box>
          )}
          componentProps={{}}
        />

        {/* Agregar recibo */}
        <CustomModal
          open={addReceiptOpen}
          handleClose={() => setAddReceiptOpen(false)}
          setOpen={setAddReceiptOpen}
          title="Agregar recibo"
          width="40%"
          component={AddReceiptToPayment}
          componentProps={{ payments: addReceiptPayments, onSaved: fetchData }}
        />

        {/* Detalle de pago */}
        <CustomModal
          open={paymentDetailOpen}
          handleClose={() => setPaymentDetailOpen(false)}
          setOpen={setPaymentDetailOpen}
          title="Detalle de pago"
          component={PaymentDetailTabbed}
          componentProps={{ data: paymentDetail }}
          width="850px"
        />

        <InvoiceOrderLinksDialog
          open={orderLinksOpen}
          invoice={selectedInvoice}
          onClose={() => setOrderLinksOpen(false)}
          onSaved={async (updatedInvoice) => {
            setSelectedInvoice(updatedInvoice);
            await fetchData();
          }}
        />

        <Dialog open={bankFlowOpen} onClose={() => setBankFlowOpen(false)} maxWidth="md" fullWidth>
          <DialogContent sx={{ p: 2.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Flujo Abasto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Factura #{bankFlowInvoice?.number || bankFlowInvoice?.id || "No disponible"}
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<AccountBalance sx={{ fontSize: 16 }} />}
                label="Abasto"
                variant="outlined"
                sx={{
                  color: '#10173c',
                  borderColor: 'rgba(16, 23, 60, 0.45)',
                  bgcolor: 'rgba(16, 23, 60, 0.05)',
                  '& .MuiChip-icon': { color: '#10173c' },
                }}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1.5}>
              {(() => {
                const bankBalance = bankFlowObligation?.balance ?? bankFlowInvoice?.abasto_obligation_balance;
                const providerName = (
                  bankFlowInvoice?.supplier?.companyName
                  || bankFlowInvoice?.supplier?.legalName
                  || bankFlowInvoice?.supplier_name
                  || (typeof bankFlowInvoice?.supplier === "string" ? bankFlowInvoice.supplier : null)
                );
                return [
                  ["Proveedor", providerName || "No disponible"],
                  ["Orden relacionada", bankFlowRequisition?.purchase_order_code || (bankFlowInvoice?.order_ids?.length ? bankFlowInvoice.order_ids.join(", ") : null)],
                  ["No. requisicion", bankFlowRequisition?.requisition_number || bankFlowObligation?.requisition_number],
                  ["Monto requisicion", bankFlowRequisition?.requested_amount ? formatQuetzales(bankFlowRequisition.requested_amount) : null],
                  ["Estado Abasto", bankPaymentStatusLabels[bankFlowInvoice?.bank_payment_status] || bankFlowInvoice?.abasto_obligation_status_label || "Sin obligacion"],
                  ["Obligacion/despacho", bankFlowObligation?.dispatch_number || (bankFlowInvoice?.abasto_obligation_id ? `Obligacion ${bankFlowInvoice.abasto_obligation_id}` : null)],
                  ["Saldo bancario", bankBalance !== null && bankBalance !== undefined ? formatQuetzales(bankBalance) : null],
                  ["Estado bancario", bankPaymentStatusLabels[bankFlowInvoice?.bank_payment_status] || "Pendiente"],
                ];
              })().map(([label, value]) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || "No disponible"}</Typography>
                </Grid>
              ))}
            </Grid>

            <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Abasto vinculado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bankFlowLoading
                      ? "Cargando..."
                      : (bankFlowRequisition?.id ? "Datos de Abasto" : "Pendiente de cargar Abasto")}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={
                    bankRequisitionStatusLabels[bankFlowRequisition?.status]
                    || bankPaymentStatusLabels[bankFlowInvoice?.bank_payment_status]
                    || "Pendiente Abasto"
                  }
                  color={bankFlowRequisition?.id ? "info" : "default"}
                  variant="outlined"
                />
              </Stack>

              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {[
                  ["Banco", bankFlowRequisition?.bank_name],
                  ["Numero", bankFlowRequisition?.requisition_number],
                  ["Fecha", bankFlowRequisition?.requisition_date ? formatYMD(bankFlowRequisition.requisition_date) : null],
                  ["Monto", bankFlowRequisition?.requested_amount ? formatQuetzales(bankFlowRequisition.requested_amount) : null],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2">{value || "No disponible"}</Typography>
                  </Grid>
                ))}
              </Grid>

              {bankFlowObligation && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Obligacion Abasto
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bankFlowObligation.dispatch_number || "Sin despacho"} - saldo {formatQuetzales(bankFlowObligation.balance || 0)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={bankFlowObligation.status_label || bankFlowInvoice?.abasto_obligation_status_label || "Abasto"}
                      color={abastoStatusColors[bankFlowObligation.internal_status] || "default"}
                      variant="outlined"
                    />
                  </Stack>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {[
                      ["Monto inicial", formatQuetzales(bankFlowObligation.initial_amount || 0)],
                      ["Pagado", formatQuetzales(bankFlowObligation.paid_amount || 0)],
                      ["Interes", formatQuetzales(bankFlowObligation.interest || 0)],
                      ["Mora", formatQuetzales(bankFlowObligation.late_fee || 0)],
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} md={3} key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {bankFlowRequisition?.notes && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Notas</Typography>
                  <Typography variant="body2">{bankFlowRequisition.notes}</Typography>
                </Box>
              )}

              {(bankFlowRequisition?.document_url || bankFlowRequisition?.document) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    size="small"
                    startIcon={<Preview />}
                    onClick={() => window.open(bankFlowRequisition.document_url || bankFlowRequisition.document, "_blank", "noopener,noreferrer")}
                    sx={{ textTransform: "none" }}
                  >
                    Ver documento
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    component="a"
                    href={bankFlowRequisition.document_url || bankFlowRequisition.document}
                    target="_blank"
                    rel="noreferrer"
                    download
                    sx={{ textTransform: "none" }}
                  >
                    Descargar
                  </Button>
                </Stack>
              )}
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              variant="contained"
              startIcon={<AccountBalance />}
              onClick={() => {
                setBankFlowOpen(false);
                openAbastoPage();
              }}
            >
              {bankFlowInvoice?.abasto_obligation_id ? "Ver Abasto" : "Crear obligacion"}
            </Button>
            <Button size="small" onClick={() => setBankFlowOpen(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Editar factura */}
        <CustomModal
          open={openEdit}
          setOpen={setOpenEdit}
          handleClose={() => setOpenEdit(false)}
          component={EditInvoice}
          width="80%"
          title="Editar factura"
          icon={Edit}
          componentProps={{
            invoiceId: selectedInvoiceId,
            onSaved: () => fetchData(),
          }}
        />
      </Box>
    </>
  );
}

/* ----------------------- Subcomponente ----------------------- */
function AddReceiptToPayment({ setOpen, payments = [], onSaved }) {
  const [paymentId, setPaymentId] = React.useState(payments[0]?.id || null);
  const [saving, setSaving] = React.useState(false);

  const handleSave = async (rc) => {
    if (!paymentId) return;
    try {
      setSaving(true);
      const payload = {
        number: rc.number || '',
        date: toISODate(rc.date),
        amount: String(Number(rc.amount || 0).toFixed(2)),
        receipt_type: rc.rType || rc.type || 'electronico',
        is_physical: !!rc.is_ph,
      };
      await SettlementsAPI.addReceipt(paymentId, payload);
      onSaved?.();
      setOpen(false);
    } catch (e) {
      console.error('Error agregando recibo', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <FormControl size='small' fullWidth sx={{ mb: 1 }}>
        <InputLabel id='pay-select'>Pago</InputLabel>
        <Select labelId='pay-select' label='Pago' value={paymentId || ''} onChange={(e) => setPaymentId(e.target.value)}>
          {(payments || []).map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.label + " (" + ((p.applied || 0).toFixed(2)) + ")"}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <ReceiptForm setOpen={setOpen} onSave={handleSave} saving={saving} />
    </Box>
  );
}
